import { useCallback, useEffect, useRef, useState } from 'react'
import { Editor } from '@tiptap/react'
import { CellSelection } from '@tiptap/pm/tables'
import { computeCellSelectionCounts } from './computeCellSelectionCounts'
import { isPosInCellSelection } from './isPosInCellSelection'

export type TableContextMenuState = {
  cell: HTMLElement
  position: { top: number; left: number }
  rowCount: number
  columnCount: number
  dataRowCount: number
  tableColumnCount: number
}

export const useTableContextMenu = (
  editor: Editor | null,
  containerEl: HTMLElement | null
) => {
  const [contextMenu, setContextMenu] = useState<TableContextMenuState | null>(
    null
  )
  const menuRootRef = useRef<HTMLDivElement | null>(null)
  // 範囲内右クリック時の mousedown 時点での CellSelection を保持しておく。
  // ブラウザ・ProseMirror の標準挙動で、右クリック後に selection が右クリック位置へ
  // 潰されることがあるため、contextmenu ハンドラ側で復元できるよう控えておく。
  const savedCellSelectionRef = useRef<CellSelection | null>(null)

  useEffect(() => {
    if (!containerEl) return undefined

    const handleContextMenu = (event: MouseEvent) => {
      const el = event.target instanceof Element ? event.target : null
      const cell = el?.closest('td, th')
      if (!(cell instanceof HTMLElement) || !containerEl.contains(cell)) return
      event.preventDefault()

      // mousedown 時に保存していた CellSelection が現在の selection と異なる
      // （= 右クリックで selection が潰された）場合は復元する。
      if (editor && savedCellSelectionRef.current) {
        const saved = savedCellSelectionRef.current
        if (!editor.state.selection.eq(saved)) {
          const $anchorCell = editor.state.doc.resolve(saved.$anchorCell.pos)
          const $headCell = editor.state.doc.resolve(saved.$headCell.pos)
          const tr = editor.state.tr.setSelection(
            new CellSelection($anchorCell, $headCell)
          )
          editor.view.dispatch(tr)
        }
      }
      savedCellSelectionRef.current = null

      // dataRowCount は td を含む行の数。「行を削除」を実行して削除対象がデータ行
      // すべてを覆うと、表が消える or ヘッダーだけ残るためメニューに出さない。
      const tableEl = cell.closest('table')
      let dataRowCount = 0
      let tableColumnCount = 1
      if (tableEl instanceof HTMLTableElement) {
        tableColumnCount = tableEl.rows[0]?.cells.length ?? 1
        dataRowCount = Array.from(tableEl.rows).filter(row =>
          Array.from(row.cells).some(c => c.tagName === 'TD')
        ).length
      }

      // 複数セル選択中で右クリックセルがその範囲内なら、削除対象の行数・列数を計算する。
      let rowCount = 1
      let columnCount = 1
      if (editor) {
        const pos = editor.view.posAtDOM(cell, 0)
        const sel = editor.state.selection
        if (
          pos >= 0 &&
          sel instanceof CellSelection &&
          isPosInCellSelection(sel, pos)
        ) {
          const counts = computeCellSelectionCounts(sel)
          rowCount = counts.rowCount
          columnCount = counts.columnCount
        }
      }

      setContextMenu({
        cell,
        position: { top: event.clientY, left: event.clientX },
        rowCount,
        columnCount,
        dataRowCount,
        tableColumnCount,
      })
    }

    // 右クリック対象セルが現在の CellSelection 範囲内なら、その selection を保存して
    // preventDefault を試みる。preventDefault が効かないブラウザ／経路でも、
    // contextmenu ハンドラで savedCellSelectionRef から復元される。
    const handleMouseDown = (event: MouseEvent) => {
      savedCellSelectionRef.current = null
      if (event.button !== 2 || !editor) return
      const el = event.target instanceof Element ? event.target : null
      const cell = el?.closest('td, th')
      if (!(cell instanceof HTMLElement) || !containerEl.contains(cell)) return
      const pos = editor.view.posAtDOM(cell, 0)
      if (pos < 0) return
      const sel = editor.state.selection
      if (sel instanceof CellSelection && isPosInCellSelection(sel, pos)) {
        savedCellSelectionRef.current = sel
        event.preventDefault()
      }
    }

    containerEl.addEventListener('contextmenu', handleContextMenu)
    containerEl.addEventListener('mousedown', handleMouseDown)
    return () => {
      containerEl.removeEventListener('contextmenu', handleContextMenu)
      containerEl.removeEventListener('mousedown', handleMouseDown)
    }
  }, [containerEl, editor])

  // メニュー外クリックで閉じる。
  // Popper は createPortal で document.body 配下に出るため MUI の ClickAwayListener では
  // ref が portal 越しに解決されず外側判定が機能しないので、document に直接 listener を貼る。
  useEffect(() => {
    if (!contextMenu) return undefined
    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target instanceof Node ? event.target : null
      if (target && menuRootRef.current?.contains(target)) return
      setContextMenu(null)
    }
    // メニューを開いた右クリックの mouseup/click と同じイベントループで listener を貼ると
    // 直後に自分を閉じてしまう。次の tick で登録して回避する。
    const timerId = window.setTimeout(() => {
      document.addEventListener('mousedown', handleDocumentMouseDown)
    }, 0)
    return () => {
      window.clearTimeout(timerId)
      document.removeEventListener('mousedown', handleDocumentMouseDown)
    }
  }, [contextMenu])

  // スクロール・リサイズで位置がズレるのでメニューを消す
  useEffect(() => {
    if (!contextMenu) return undefined
    const handleReposition = () => setContextMenu(null)
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)
    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [contextMenu])

  const closeContextMenu = useCallback(() => setContextMenu(null), [])

  return { contextMenu, closeContextMenu, menuRootRef }
}
